import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES } from "@/components/StatusBadge";
import { Plus, Trash2, ArrowLeft, Save, Link as LinkIcon, FileUp, X } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: "", objective: "", hypothesis: "",
  materials: [""], protocol: [{ order: 1, text: "" }],
  status: "Planned", start_date: "", end_date: "",
  tags: [], project_id: null, is_template: false,
  references: [], remarks: "",
};

export default function ExperimentForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [projects, setProjects] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    if (!isNew) {
      api.get(`/experiments/${id}`).then((r) => {
        const d = r.data;
        setForm({
          ...empty,
          ...d,
          materials: d.materials?.length ? d.materials : [""],
          protocol: d.protocol?.length ? d.protocol : [{ order: 1, text: "" }],
          start_date: d.start_date || "",
          end_date: d.end_date || "",
        });
      });
    }
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        materials: form.materials.filter((m) => m.trim()),
        protocol: form.protocol
          .filter((p) => p.text.trim())
          .map((p, i) => ({ order: i + 1, text: p.text })),
        project_id: form.project_id || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        references: form.references || [],
        remarks: form.remarks || "",
      };
      if (isNew) {
        const { data } = await api.post("/experiments", payload);
        toast.success("Experiment created");
        navigate(`/experiments/${data.id}`);
      } else {
        await api.put(`/experiments/${id}`, payload);
        toast.success("Changes saved");
        navigate(`/experiments/${id}`);
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      update("tags", [...form.tags, tagInput.trim()]);
    }
    setTagInput("");
  };

  // References
  const [refLink, setRefLink] = useState({ label: "", url: "" });
  const refFileInput = React.useRef();
  const addRefLink = () => {
    if (!refLink.url.trim()) return toast.error("URL required");
    update("references", [...(form.references || []), {
      kind: "link", label: refLink.label || refLink.url, url: refLink.url,
      added_at: new Date().toISOString(),
    }]);
    setRefLink({ label: "", url: "" });
  };
  const onRefFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const newRefs = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newRefs.push({
          kind: "file", label: data.original_filename, file_id: data.id,
          filename: data.original_filename, added_at: new Date().toISOString(),
        });
      }
      update("references", [...(form.references || []), ...newRefs]);
      toast.success(`${newRefs.length} reference(s) added`);
    } catch { toast.error("Upload failed"); }
    if (refFileInput.current) refFileInput.current.value = "";
  };
  const removeRef = (i) => update("references", form.references.filter((_, idx) => idx !== i));

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="experiment-form-page">
      <div className="flex items-center justify-between">
        <Link to={isNew ? "/experiments" : `/experiments/${id}`} className="text-sm font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-experiment-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
        </Button>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">
          {isNew ? "New experiment" : "Edit experiment"}
        </div>
        <Input
          value={form.title}
          onChange={(e) => update("title", e.target.value)}
          placeholder="Untitled experiment"
          className="text-3xl font-sans font-bold tracking-tight border-0 shadow-none focus-visible:ring-0 px-0 h-auto py-2 mt-1 bg-transparent"
          data-testid="experiment-title-input"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger data-testid="experiment-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Start date">
          <Input type="date" value={form.start_date || ""} onChange={(e) => update("start_date", e.target.value)} data-testid="experiment-start-date" />
        </Field>
        <Field label="End date">
          <Input type="date" value={form.end_date || ""} onChange={(e) => update("end_date", e.target.value)} data-testid="experiment-end-date" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Project">
          <Select
            value={form.project_id || "none"}
            onValueChange={(v) => update("project_id", v === "none" ? null : v)}
          >
            <SelectTrigger data-testid="experiment-project-select"><SelectValue placeholder="No project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No project</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Save as template">
          <div className="flex items-center h-10 gap-2">
            <input
              id="is-template"
              type="checkbox"
              checked={!!form.is_template}
              onChange={(e) => update("is_template", e.target.checked)}
              className="h-4 w-4 accent-blue-600"
              data-testid="experiment-is-template"
            />
            <label htmlFor="is-template" className="text-sm font-body text-zinc-600 dark:text-zinc-400">
              Mark this as a reusable template
            </label>
          </div>
        </Field>
      </div>

      <Field label="Objective">
        <Textarea rows={2} value={form.objective} onChange={(e) => update("objective", e.target.value)} placeholder="What question does this experiment answer?" data-testid="experiment-objective-input" />
      </Field>

      <Field label="Hypothesis">
        <Textarea rows={2} value={form.hypothesis} onChange={(e) => update("hypothesis", e.target.value)} placeholder="What do you expect to happen and why?" data-testid="experiment-hypothesis-input" />
      </Field>

      {/* Materials */}
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Materials</Label>
        <div className="space-y-2 mt-2">
          {form.materials.map((m, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs font-mono text-zinc-400 mt-2.5 w-6">{i + 1}.</span>
              <Input
                value={m}
                onChange={(e) => {
                  const arr = [...form.materials]; arr[i] = e.target.value; update("materials", arr);
                }}
                placeholder="e.g. Taq polymerase, 10 µM primers…"
                data-testid={`material-input-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => {
                const arr = form.materials.filter((_, idx) => idx !== i);
                update("materials", arr.length ? arr : [""]);
              }}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => update("materials", [...form.materials, ""])} data-testid="add-material-btn">
            <Plus className="h-4 w-4 mr-1" /> Add material
          </Button>
        </div>
      </div>

      {/* Protocol */}
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Protocol</Label>
        <div className="space-y-2 mt-2">
          {form.protocol.map((step, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs font-mono text-zinc-400 mt-2.5 w-6">{i + 1}.</span>
              <Textarea
                rows={2}
                value={step.text}
                onChange={(e) => {
                  const arr = [...form.protocol];
                  arr[i] = { ...arr[i], text: e.target.value, order: i + 1 };
                  update("protocol", arr);
                }}
                placeholder={`Step ${i + 1}`}
                data-testid={`protocol-step-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => {
                const arr = form.protocol.filter((_, idx) => idx !== i);
                update("protocol", arr.length ? arr : [{ order: 1, text: "" }]);
              }}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => update("protocol", [...form.protocol, { order: form.protocol.length + 1, text: "" }])} data-testid="add-step-btn">
            <Plus className="h-4 w-4 mr-1" /> Add step
          </Button>
        </div>
      </div>

      {/* References */}
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">References</Label>
        <div className="space-y-2 mt-2">
          {(form.references || []).map((r, i) => (
            <div key={i} className="flex items-center gap-2 border border-zinc-200 dark:border-zinc-800 rounded-md px-3 py-2 bg-zinc-50 dark:bg-zinc-900/40" data-testid={`reference-${i}`}>
              {r.kind === "link" ? <LinkIcon className="h-4 w-4 text-blue-600 flex-shrink-0" /> : <FileUp className="h-4 w-4 text-blue-600 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-body text-sm truncate">{r.label || r.url || r.filename}</div>
                {r.url && <div className="text-xs font-mono text-zinc-500 truncate">{r.url}</div>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeRef(i)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input value={refLink.label} onChange={(e) => setRefLink({ ...refLink, label: e.target.value })} placeholder="Reference label (optional)" data-testid="ref-label-input" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <Input value={refLink.url} onChange={(e) => setRefLink({ ...refLink, url: e.target.value })} placeholder="https://doi.org/..." data-testid="ref-url-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRefLink(); } }} />
            </div>
            <Button variant="outline" size="sm" onClick={addRefLink} data-testid="add-ref-link-btn"><LinkIcon className="h-4 w-4 mr-1" /> Add link</Button>
            <input type="file" ref={refFileInput} onChange={onRefFile} multiple hidden accept=".pdf,.doc,.docx,.txt" />
            <Button variant="outline" size="sm" onClick={() => refFileInput.current?.click()} data-testid="add-ref-file-btn"><FileUp className="h-4 w-4 mr-1" /> Upload PDF</Button>
          </div>
        </div>
      </div>

      {/* Remarks */}
      <Field label="Remarks">
        <Textarea
          rows={3}
          value={form.remarks || ""}
          onChange={(e) => update("remarks", e.target.value)}
          placeholder="Free-form notes, caveats, things to revisit later…"
          data-testid="experiment-remarks-input"
        />
      </Field>

      {/* Tags */}
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tags</Label>
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
            placeholder="Add tag and press Enter"
            className="max-w-xs"
            data-testid="tag-input"
          />
          <Button variant="outline" size="sm" onClick={addTag}>Add</Button>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {form.tags.map((t) => (
              <span key={t} className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 rounded px-2 py-0.5 flex items-center gap-1">
                #{t}
                <button onClick={() => update("tags", form.tags.filter((x) => x !== t))} className="text-zinc-400 hover:text-red-500">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
